// Types that are part of the public developer API.
// Import from here when building schemas or handling events exposed by the library.

// ── File import events ────────────────────────────────────────────────────────

export interface FileInfo {
  sizeBytes: number;
  fileType: string;
}

export type FileInfoArr = Array<FileInfo>;

// ── API errors ────────────────────────────────────────────────────────────────

/** Error shape returned by PHP and rethrown by the Paramate SDK (e.g. status 499 = aborted) */
export interface PhpApiError {
  status: number;
  requestId?: string;
  response?: { message?: string };
}

// ── Changelog & notifications ─────────────────────────────────────────────────

/** `msg` is a plain string or { prod, dev } to show different text per environment */
export interface ChangelogEntry {
  dateUnixMs?: number;
  msg: string | { prod: string; dev: string };
}

export type Changelog = Record<string, ChangelogEntry>;

/** Multi-source changelog keyed by source name (e.g. "app", "trcadScript") */
export type MultiChangelog = Record<string, Changelog>;

export interface StoredNotification {
  id: string;
  defaultText: string;
  dictKey: string;
  type: 'success' | 'error' | 'warning' | 'info';
  timestamp: number;
}

// ── Export schema types ───────────────────────────────────────────────────────

export const MESH_TYPES = ['stl', '3mf', 'stp'] as const;
export type MeshType = typeof MESH_TYPES[number];

/** Dispatched on window as 'as-export-status'. operation is owned by the caller ('download', 'grabcad', etc.). */
export interface ExportStatusDetail {
  exporterId: string;
  operation:  string;
  state:      'pending' | 'success' | 'error';
  message?:   string;
}

/** Dispatched on window as 'as-export-progress' once per file resolved inside ExportBuilder.resolve(). */
export interface ExportProgressDetail {
  exporterId: string;
  current: number;
  total:   number;
}

export type FileCategory =
  | 'workpiece'
  | 'outputPart' // deprecated — use 'trcadMesh'
  | 'trcadMesh'
  | 'brep'
  | 'visualization'
  | 'document'
  | 'custom'
  | 'configurator';

/**
 * Declarative schema for a single file entry in an ExportBuilder.
 *
 * - `data`: pre-set bytes OR an async function that provides them at export time.
 *   If omitted for mesh categories (`outputPart`, `workpiece`, `brep`), bytes are
 *   auto-fetched from as-configurator at resolve time.
 *   `trcadMesh` is the preferred category for configurator output parts.
 * - `finalize`: controls whether the configurator's finalized mesh is fetched (default `true`).
 *   Only meaningful for auto-fetched mesh categories.
 * - `beforeExport`: called before data is resolved. Use it to configure session state
 *   on as-configurator (e.g. set params for the upcoming mesh fetch).
 * - `children`: dep files nested under this entry (equivalent to passing the returned
 *   index as `parentIdx`).
 */
export type FileDataSchema = {
  fileKey?: string;
  fileName?: string;
  category: FileCategory;
  /** Format of the provided data, or target format for auto-fetch. */
  fileType?: string;
  /** Whether to fetch the finalized configurator mesh. Default `true`. Only applies to auto-fetched mesh categories. */
  finalize?: boolean;
  customData?: Record<string, any>;
  tags?: string[];
  /** Called before data is resolved. Use it to configure session state on as-configurator. */
  beforeExport?: () => Promise<void>;
  /** Pre-set bytes (ArrayBuffer or Blob), or an async function returning either.
   *  Blobs are converted to ArrayBuffer automatically.
   *  Overrides the default configurator auto-fetch for mesh categories. */
  data?: ArrayBuffer | Blob | (() => Promise<ArrayBuffer | Blob>);
  children?: FileDataSchema[];
};

